import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, TabBar } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type Categoria = {
  id_categoria: number;
  nome_categoria: string;
  parent_id: number | null;
  icon: string | null;
  sort_order: number;
};

type Provincia = {
  id_provincia: number;
  nome: string;
  sigla: string;
};

type Comune = {
  comune_id: string;
  comune_nome: string;
  id_provincia: number;
  provincia_nome: string;
  provincia_sigla: string;
};

type Tab = 'categorie' | 'province' | 'comuni';

export function MasterPortaleGeographyPage() {
  const [tab, setTab] = useState<Tab>('categorie');

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Geografia & Categorie"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · gestione province, comuni e categorie gerarchiche del portale.
          </>
        }
      />

      <TabBar
        tabs={[
          { key: 'categorie', label: 'Categorie' },
          { key: 'province', label: 'Province' },
          { key: 'comuni', label: 'Comuni' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'categorie' && <CategorieTab />}
      {tab === 'province' && <ProvinceTab />}
      {tab === 'comuni' && <ComuniTab />}
    </section>
  );
}

// ============================================================
// Categorie (gerarchiche)
// ============================================================

function CategorieTab() {
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Form nuovo
  const [nome, setNome] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [icon, setIcon] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/categorie');
      const data = await res.json().catch(() => []) as Categoria[];
      setCategorie(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const padri = categorie.filter((c) => !c.parent_id);
  const figli = (parentIdNum: number) => categorie.filter((c) => c.parent_id === parentIdNum);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/categorie', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          nome_categoria: nome.trim(),
          parent_id: parentId ? Number(parentId) : null,
          icon: icon.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNome('');
      setParentId('');
      setIcon('');
      setMsg('Categoria creata');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: number) {
    if (!confirm('Eliminare questa categoria? Le sottocategorie e le card associate potrebbero rimanere orfane.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/v1/portale/master/categorie/${id}`, { method: 'DELETE' });
      setMsg('Categoria eliminata');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onMove(id: number, newParentId: number | null) {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const res = await apiFetch(`/api/v1/portale/master/categorie/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ parent_id: newParentId }),
      });
      const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setMsg(newParentId ? 'Categoria spostata' : 'Categoria promossa a padre');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error ? <p className="sk-error">{error}</p> : null}
      {msg ? <p className="sk-status">{msg}</p> : null}

      <form className="sk-saas-form" onSubmit={onCreate} style={{ marginBottom: '2rem' }}>
        <h3 className="sk-section-title">Nuova categoria</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label className="sk-field" style={{ flex: '2 1 200px' }}>
            <span>Nome</span>
            <input className="sk-input" value={nome} onChange={(e) => setNome(e.target.value)} required placeholder="es. Ristoranti" />
          </label>
          <label className="sk-field" style={{ flex: '1 1 150px' }}>
            <span>Categoria padre (opzionale)</span>
            <select className="sk-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— Nessuna (padre) —</option>
              {padri.map((p) => (
                <option key={p.id_categoria} value={p.id_categoria}>{p.nome_categoria}</option>
              ))}
            </select>
          </label>
          <label className="sk-field" style={{ flex: '1 1 100px' }}>
            <span>Icona (opzionale)</span>
            <input className="sk-input" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="utensils" />
          </label>
        </div>
        <button type="submit" className="sk-btn sk-btn--primary" disabled={busy}>
          {busy ? '…' : 'Aggiungi categoria'}
        </button>
      </form>

      <h3 className="sk-section-title">Categorie esistenti ({categorie.length})</h3>
      {padri.length === 0 ? (
        <p className="sk-muted">Nessuna categoria. Crea la prima sopra.</p>
      ) : (
        <div className="sk-categorie-tree">
          {padri.map((padre) => (
            <div key={padre.id_categoria} className="sk-categorie-group">
              <div className="sk-categorie-row sk-categorie-row--padre">
                <span className="sk-categorie-icon">{padre.icon || '📁'}</span>
                <strong>{padre.nome_categoria}</strong>
                <span className="sk-muted">padre</span>
                <select
                  className="sk-input sk-categorie__move"
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    onMove(padre.id_categoria, val ? Number(val) : null);
                  }}
                  disabled={busy}
                  title="Sposta sotto un altro padre"
                >
                  <option value="">Sposta…</option>
                  <option value="">— Promuovi a padre (già padre) —</option>
                  {padri.filter((p) => p.id_categoria !== padre.id_categoria).map((p) => (
                    <option key={p.id_categoria} value={p.id_categoria}>
                      → {p.nome_categoria}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet sk-btn--sm"
                  onClick={() => onDelete(padre.id_categoria)}
                >
                  Elimina
                </button>
              </div>
              {figli(padre.id_categoria).map((figlio) => (
                <div key={figlio.id_categoria} className="sk-categorie-row sk-categorie-row--figlio">
                  <span className="sk-categorie-icon">{figlio.icon || '•'}</span>
                  <span>{figlio.nome_categoria}</span>
                  <span className="sk-muted">sotto: {padre.nome_categoria}</span>
                  <select
                    className="sk-input sk-categorie__move"
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      onMove(figlio.id_categoria, val ? Number(val) : null);
                    }}
                    disabled={busy}
                    title="Sposta o promuovi a padre"
                  >
                    <option value="">Sposta…</option>
                    <option value="">— Promuovi a padre —</option>
                    {padri.filter((p) => p.id_categoria !== padre.id_categoria).map((p) => (
                      <option key={p.id_categoria} value={p.id_categoria}>
                        → {p.nome_categoria}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="sk-btn sk-btn--quiet sk-btn--sm"
                    onClick={() => onDelete(figlio.id_categoria)}
                  >
                    Elimina
                  </button>
                </div>
              ))}
            </div>
          ))}
          {/* Categorie orfane (parent_id non valido) */}
          {categorie.filter((c) => c.parent_id && !padri.some((p) => p.id_categoria === c.parent_id)).map((orphan) => (
            <div key={orphan.id_categoria} className="sk-categorie-row sk-categorie-row--figlio">
              <span className="sk-categorie-icon">{orphan.icon || '•'}</span>
              <span>{orphan.nome_categoria}</span>
              <span className="sk-muted" style={{ color: 'var(--sk-warn, #c30)' }}>orfana</span>
              <select
                className="sk-input sk-categorie__move"
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  onMove(orphan.id_categoria, val ? Number(val) : null);
                }}
                disabled={busy}
                title="Sposta o promuovi a padre"
              >
                <option value="">Sposta…</option>
                <option value="">— Promuovi a padre —</option>
                {padri.map((p) => (
                  <option key={p.id_categoria} value={p.id_categoria}>
                    → {p.nome_categoria}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="sk-btn sk-btn--quiet sk-btn--sm"
                onClick={() => onDelete(orphan.id_categoria)}
              >
                Elimina
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Province
// ============================================================

function ProvinceTab() {
  const [province, setProvince] = useState<Provincia[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [sigla, setSigla] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/v1/portale/master/province');
      const data = await res.json().catch(() => []) as Provincia[];
      setProvince(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/province', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ nome: nome.trim(), sigla: sigla.trim().toUpperCase() }),
      });
      const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNome('');
      setSigla('');
      setMsg('Provincia creata');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: number) {
    if (!confirm('Eliminare questa provincia? I comuni associati rimarranno orfani.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/v1/portale/master/province/${id}`, { method: 'DELETE' });
      setMsg('Provincia eliminata');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error ? <p className="sk-error">{error}</p> : null}
      {msg ? <p className="sk-status">{msg}</p> : null}

      <form className="sk-saas-form" onSubmit={onCreate} style={{ marginBottom: '2rem' }}>
        <h3 className="sk-section-title">Nuova provincia</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label className="sk-field" style={{ flex: '2 1 200px' }}>
            <span>Nome</span>
            <input className="sk-input" value={nome} onChange={(e) => setNome(e.target.value)} required placeholder="es. Pavia" />
          </label>
          <label className="sk-field" style={{ flex: '1 1 80px' }}>
            <span>Sigla</span>
            <input
              className="sk-input"
              value={sigla}
              onChange={(e) => setSigla(e.target.value.toUpperCase().slice(0, 2))}
              required
              maxLength={2}
              placeholder="PV"
              style={{ textTransform: 'uppercase' }}
            />
          </label>
        </div>
        <button type="submit" className="sk-btn sk-btn--primary" disabled={busy}>
          {busy ? '…' : 'Aggiungi provincia'}
        </button>
      </form>

      <h3 className="sk-section-title">Province esistenti ({province.length})</h3>
      <table className="sk-table">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Sigla</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {province.map((p) => (
            <tr key={p.id_provincia}>
              <td>{p.nome}</td>
              <td><strong>{p.sigla}</strong></td>
              <td>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet sk-btn--sm"
                  onClick={() => onDelete(p.id_provincia)}
                >
                  Elimina
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Comuni
// ============================================================

function ComuniTab() {
  const [comuni, setComuni] = useState<Comune[]>([]);
  const [province, setProvince] = useState<Provincia[]>([]);
  const [filterProvincia, setFilterProvincia] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [selectedProvincia, setSelectedProvincia] = useState<string>('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [provRes, comRes] = await Promise.all([
        apiFetch('/api/v1/portale/master/province'),
        apiFetch(`/api/v1/portale/master/comuni${filterProvincia ? `?provincia=${filterProvincia}` : ''}`),
      ]);
      const provData = await provRes.json().catch(() => []) as Provincia[];
      const comData = await comRes.json().catch(() => []) as Comune[];
      setProvince(Array.isArray(provData) ? provData : []);
      setComuni(Array.isArray(comData) ? comData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [filterProvincia]);

  useEffect(() => { void load(); }, [load]);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!selectedProvincia) {
      setError('Seleziona una provincia');
      return;
    }
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const res = await apiFetch('/api/v1/portale/master/comuni', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          comune_nome: nome.trim(),
          id_provincia: Number(selectedProvincia),
        }),
      });
      const body = await res.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setNome('');
      setMsg('Comune creato');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm('Eliminare questo comune? Le card associate potrebbero rimanere orfane.')) return;
    setBusy(true);
    try {
      await apiFetch(`/api/v1/portale/master/comuni/${id}`, { method: 'DELETE' });
      setMsg('Comune eliminato');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error ? <p className="sk-error">{error}</p> : null}
      {msg ? <p className="sk-status">{msg}</p> : null}

      <form className="sk-saas-form" onSubmit={onCreate} style={{ marginBottom: '2rem' }}>
        <h3 className="sk-section-title">Nuovo comune</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label className="sk-field" style={{ flex: '2 1 200px' }}>
            <span>Nome comune</span>
            <input className="sk-input" value={nome} onChange={(e) => setNome(e.target.value)} required placeholder="es. Brescia" />
          </label>
          <label className="sk-field" style={{ flex: '1 1 150px' }}>
            <span>Provincia</span>
            <select className="sk-input" value={selectedProvincia} onChange={(e) => setSelectedProvincia(e.target.value)} required>
              <option value="">— Seleziona —</option>
              {province.map((p) => (
                <option key={p.id_provincia} value={p.id_provincia}>{p.nome} ({p.sigla})</option>
              ))}
            </select>
          </label>
        </div>
        <button type="submit" className="sk-btn sk-btn--primary" disabled={busy}>
          {busy ? '…' : 'Aggiungi comune'}
        </button>
      </form>

      <div style={{ marginBottom: '1rem' }}>
        <label className="sk-field" style={{ maxWidth: '300px' }}>
          <span>Filtra per provincia</span>
          <select className="sk-input" value={filterProvincia} onChange={(e) => setFilterProvincia(e.target.value)}>
            <option value="">Tutte</option>
            {province.map((p) => (
              <option key={p.id_provincia} value={p.id_provincia}>{p.nome} ({p.sigla})</option>
            ))}
          </select>
        </label>
      </div>

      <h3 className="sk-section-title">Comuni ({comuni.length})</h3>
      <table className="sk-table">
        <thead>
          <tr>
            <th>Comune</th>
            <th>Provincia</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {comuni.map((c) => (
            <tr key={c.comune_id}>
              <td>{c.comune_nome}</td>
              <td>{c.provincia_nome} ({c.provincia_sigla})</td>
              <td>
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet sk-btn--sm"
                  onClick={() => onDelete(c.comune_id)}
                >
                  Elimina
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
